import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    InputGroup,
    Spinner,
    Alert,
    Dropdown,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import SEO from '@/components/common/SEO';
import './AdminDashboard.css';

const PAGE_SIZE = 10;

const ROLE_CONFIG = {
    admin: { label: 'Admin', badgeClass: 'admin-role-badge-admin' },
    researcher: {
        label: 'Researcher',
        badgeClass: 'admin-role-badge-researcher',
    },
    user: { label: 'User', badgeClass: 'admin-role-badge-user' },
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

function UserAvatar({ name = '', email = '' }) {
    const initial = name?.trim()?.[0] || email?.[0] || '?';
    return (
        <span
            className="admin-user-avatar bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold"
            aria-hidden="true"
        >
            {initial.toUpperCase()}
        </span>
    );
}

UserAvatar.propTypes = {
    name: PropTypes.string,
    email: PropTypes.string,
};

function RoleBadge({ role }) {
    const config = ROLE_CONFIG[role] || ROLE_CONFIG.user;
    return (
        <span className={`admin-role-badge ${config.badgeClass}`}>
            {config.label}
        </span>
    );
}

RoleBadge.propTypes = {
    role: PropTypes.string.isRequired,
};

export default function UsersAdmin() {
    const api = useAuthenticatedAxios();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [page, setPage] = useState(1);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await api.get('/api/users');
            setUsers(res.data);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            setError(err.response?.data?.message || 'Failed to load users.');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return users.filter((u) => {
            const fullName =
                `${u.name?.first || ''} ${u.name?.last || ''}`.trim();
            const matchesSearch =
                !q ||
                fullName.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q) ||
                u.username?.toLowerCase().includes(q);
            const matchesRole = roleFilter === 'all' || u.role === roleFilter;
            return matchesSearch && matchesRole;
        });
    }, [users, search, roleFilter]);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Reset to page 1 when filters change
    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleRoleFilter = (role) => {
        setRoleFilter(role);
        setPage(1);
    };

    const roleFilterLabel =
        roleFilter === 'all'
            ? 'All Roles'
            : ROLE_CONFIG[roleFilter]?.label || roleFilter;

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" variant="light" />
                <span className="visually-hidden">Loading users...</span>
            </div>
        );
    }

    return (
        <Container
            as="main"
            aria-labelledby="users-admin-heading"
            className="py-4"
        >
            <SEO title="Admin: User Management" />
            <div className="mb-4">
                <h1
                    id="users-admin-heading"
                    className="fs-4 fw-bold text-white mb-1"
                >
                    User Management
                </h1>
                <p className="text-white mb-0 small">
                    Manage user accounts, roles, and permissions
                </p>
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                    className="mb-3"
                >
                    {error}
                </Alert>
            )}

            {/* Search + filter bar */}
            <Card className="shadow-sm mb-3">
                <Card.Body className="p-3">
                    <Row className="g-2 align-items-center">
                        <Col>
                            <InputGroup>
                                <InputGroup.Text className="bg-white border-end-0">
                                    <i
                                        className="bi bi-search text-secondary"
                                        aria-hidden="true"
                                    />
                                </InputGroup.Text>
                                <Form.Control
                                    type="search"
                                    placeholder="Search by name or email..."
                                    value={search}
                                    onChange={handleSearch}
                                    className="border-start-0 ps-0"
                                    aria-label="Search users by name or email"
                                />
                            </InputGroup>
                        </Col>
                        <Col xs="auto">
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    id="role-filter-dropdown"
                                    className="d-flex align-items-center gap-2"
                                >
                                    <i
                                        className="bi bi-funnel"
                                        aria-hidden="true"
                                    />
                                    {roleFilterLabel}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        active={roleFilter === 'all'}
                                        onClick={() => handleRoleFilter('all')}
                                    >
                                        All Roles
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    {Object.entries(ROLE_CONFIG).map(
                                        ([key, cfg]) => (
                                            <Dropdown.Item
                                                key={key}
                                                active={roleFilter === key}
                                                onClick={() =>
                                                    handleRoleFilter(key)
                                                }
                                            >
                                                {cfg.label}
                                            </Dropdown.Item>
                                        )
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Col>
                    </Row>
                    <p className="text-secondary small mb-0 mt-2">
                        Showing{' '}
                        {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}
                        {filtered.length > 1
                            ? `–${Math.min(page * PAGE_SIZE, filtered.length)}`
                            : ''}{' '}
                        of {filtered.length}{' '}
                        {filtered.length === 1 ? 'user' : 'users'}
                    </p>
                </Card.Body>
            </Card>

            {/* User table */}
            <Card className="shadow-sm">
                <div className="table-responsive">
                    <table
                        className="table table-hover mb-0 align-middle"
                        aria-label="User accounts"
                    >
                        <thead>
                            <tr className="text-secondary small">
                                <th
                                    scope="col"
                                    className="ps-4 py-3 fw-semibold border-0"
                                >
                                    USER
                                </th>
                                <th
                                    scope="col"
                                    className="py-3 fw-semibold border-0"
                                >
                                    ROLE
                                </th>
                                <th
                                    scope="col"
                                    className="py-3 fw-semibold border-0 d-none d-lg-table-cell"
                                >
                                    LAST UPDATED
                                </th>
                                <th
                                    scope="col"
                                    className="py-3 fw-semibold border-0 text-end pe-4"
                                >
                                    ACTIONS
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan="4"
                                        className="text-center text-secondary py-5"
                                    >
                                        No users found.
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((u) => {
                                    const fullName =
                                        `${u.name?.first || ''} ${u.name?.last || ''}`.trim() ||
                                        u.username;
                                    return (
                                        <tr key={u._id}>
                                            <td className="ps-4 py-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <UserAvatar
                                                        name={fullName}
                                                        email={u.email}
                                                    />
                                                    <div>
                                                        <div className="fw-medium small">
                                                            {fullName}
                                                        </div>
                                                        <div className="text-secondary small">
                                                            {u.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <RoleBadge role={u.role} />
                                            </td>
                                            <td className="text-secondary small d-none d-lg-table-cell">
                                                {formatDate(u.updatedAt)}
                                            </td>
                                            <td className="text-end pe-4">
                                                <Dropdown align="end">
                                                    <Dropdown.Toggle
                                                        variant="link"
                                                        className="text-secondary p-0 border-0"
                                                        aria-label={`Actions for ${fullName}`}
                                                    >
                                                        <i
                                                            className="bi bi-three-dots-vertical"
                                                            aria-hidden="true"
                                                        />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        {/* TODO: implement user actions (view profile, change role, disable) */}
                                                        <Dropdown.Item disabled>
                                                            View Profile
                                                        </Dropdown.Item>
                                                        <Dropdown.Item disabled>
                                                            Change Role
                                                        </Dropdown.Item>
                                                        <Dropdown.Divider />
                                                        <Dropdown.Item
                                                            disabled
                                                            className="text-danger"
                                                        >
                                                            Disable Account
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <nav
                        aria-label="User table pagination"
                        className="d-flex justify-content-between align-items-center px-4 py-3 border-top"
                    >
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setPage((p) => p - 1)}
                            disabled={page === 1}
                            aria-label="Previous page"
                        >
                            <i
                                className="bi bi-chevron-left"
                                aria-hidden="true"
                            />{' '}
                            Previous
                        </button>
                        <span className="small text-secondary">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page === totalPages}
                            aria-label="Next page"
                        >
                            Next{' '}
                            <i
                                className="bi bi-chevron-right"
                                aria-hidden="true"
                            />
                        </button>
                    </nav>
                )}
            </Card>
        </Container>
    );
}
